package net.sergeych.kilogin.client

import kotlinx.serialization.KSerializer
import net.sergeych.crypto2.SigningSecretKey
import net.sergeych.kilogin.biDecode
import net.sergeych.kiloparsec.KiloClient
import net.sergeych.kiloparsec.KiloConnectionData
import net.sergeych.kiloparsec.KiloInterface
import net.sergeych.kiloparsec.Transport

class KiloginClientBuilder<S : KiloginClientSession>(
    private val localInterface: KiloInterface<S> = KiloInterface(),
) {
    private var sessionMaker: (() -> S)? = null
    private var connectionMaker: (suspend () -> Transport.Device)? = null
    private var onSaveState: ((UByteArray?) -> Unit)? = null
    private var localIdSecreteKey: SigningSecretKey = SigningSecretKey.new()
    private var loginSaltString: String = ""

    fun session(sessionMaker: () -> S) {
        this.sessionMaker = sessionMaker
    }

    /**
     * Required: set login key salt to a string of at least 12 characters length.
     * It should be the same in all client implementations to share the same login
     * name space. Otherwise, logins will use different accounts.
     */
    var loginSalt: String
    get() = loginSaltString
    set(value) {
        if( value.length < 12 )
            throw IllegalArgumentException("salt must be at least 12 characters long")
        loginSaltString = value
    }

    fun connection(connectionMaker: suspend () -> Transport.Device) {
        this.connectionMaker = connectionMaker
    }

    /**
     * Optional handler to save provided data in some __protected storage__. If data saved, and passed back
     * when creating new [KiloginClient.create], the logged in state could be retrieved, provided server
     * didn't "forget" or clear it.
     */
    fun saveState(onSaveState: (UByteArray?) -> Unit) {
        this.onSaveState = onSaveState
    }

    fun local(f: KiloInterface<S>.() -> Unit) {
        localInterface.f()
    }

    fun <D> build(dataSerializer: KSerializer<D>, initialData: UByteArray? = null): KiloginClient<D, S> {
        check(sessionMaker != null) { "must have session {}" }
        check(connectionMaker != null) { "must have connection {}" }

        // localId could be either new or specified by user,
        // or restored from last state:
        var localId = localIdSecreteKey

        val decodedInitialData = initialData?.let {
            biDecode(ClientSecretData.serializer(dataSerializer), it)
        }?.also {
            localId = it.clientPrivateKey
        }

        if( loginSaltString.isBlank())
            throw IllegalArgumentException("login salt must be set")

        return KiloginClient<D, S>(
            KiloClient<S>(
                localInterface,
                localId,
                { KiloConnectionData(connectionMaker!!(), sessionMaker!!()) }
            ),
            localId,
            decodedInitialData,
            onSaveState,
            loginSaltString,
            dataSerializer
        )
    }


//    private val connectionData: KiloConnectionData<*>? = null
}